/**
 * Chat Store - in-game chat, indipendente dalla modalità di gioco.
 *
 * Lo stato viveva dentro multiplayer-store, che però in arena e in torneo resta
 * vuoto: quei flussi deviano game:start/game:update ai loro store PRIMA che
 * roomId/playerColor vengano scritti, così `sendChat` usciva subito e i messaggi
 * in arrivo finivano in un transcript che nessuna UI leggeva. Tenendo qui sia i
 * messaggi sia il contesto della conversazione, ChatPanel e GameSidebarTabs
 * diventano agnostici alla modalità e i tre store di partita si limitano a
 * dichiarare "di quale room stiamo parlando".
 */

import { create } from 'zustand';
import type { ChatMessage } from '@draughts/shared';
import { CHAT_CONFIG, SALUTO_PRESET_KEY, eMessaggioMio, controllaRafficaChat } from '@draughts/shared';
import { sendGameChat } from '../services/socket';

export type ChatSource = 'online' | 'arena' | 'tournament';

/** Di quale partita è la conversazione attualmente mostrata. */
export interface ChatContext {
  roomId: string | null;
  playerColor: 'white' | 'black' | null;
  /** La partita accetta messaggi (in corso o appena finita). */
  canChat: boolean;
  source: ChatSource;
}

interface ChatStore extends ChatContext {
  messages: ChatMessage[];
  isOpen: boolean;
  unreadCount: number;
  /**
   * True una volta che questo giocatore ha segnalato l'avversario in questa room.
   * Mute lato client: i messaggi dell'avversario smettono di essere renderizzati e
   * di alzare il badge. Non è un ban — il server continua a consegnarli — e
   * sopravvive di proposito a una rivincita (stesso avversario, stessa
   * conversazione), perché la rivincita riusa la stessa room.
   */
  opponentReported: boolean;
  /**
   * A partita finita l'avversario è uscito (home, scheda chiusa): chi scrive
   * scriverebbe a nessuno. Lo dice il server (`game:opponent-left`) e lo ritira
   * se l'avversario rientra (`game:opponent-returned`). Si azzera con la room.
   */
  opponentLeft: boolean;
  /**
   * La chat di questa partita e' CHIUSA: la stanza che la teneva viva e' stata
   * raccolta dal server. Lo dice il server (`game:chat-closed`), e si azzera con
   * la room.
   *
   * Prima non lo diceva nessuno: `canChat` restava `true` per sempre, quindi il
   * campo restava acceso, `send()` tornava `true`, la casella si svuotava e il
   * messaggio spariva nel vuoto — il difetto per cui «la chat si interrompe dopo
   * cinque minuti e non avvisa».
   */
  chatChiusa: boolean;
  /**
   * Quando la chat chiudera' (epoch ms), per il conto alla rovescia dell'ultimo
   * minuto. `null` fuori da quel minuto.
   *
   * Arriva dal server (`game:chat-closing`) e non si calcola qui: il timer che
   * raccoglie la stanza SI RI-ARMA — fra le due manche di un match, o con un
   * invito di rivincita ancora aperto — quindi una scadenza indovinata a fine
   * partita mentirebbe.
   */
  chatClosesAt: number | null;
  /**
   * Il saluto d'apertura dell'avversario appena ARRIVATO, candidato a comparire
   * fuori dalla chat (`SalutoBuonGioco`, sopra la barra dei comandi).
   *
   * Lo popola solo `receiveMessage`, mai `restoreMessages`: un saluto che torna
   * dal transcript dopo una riconnessione e' una riga gia' letta, e non deve far
   * comparire di nuovo la pastiglia.
   */
  salutoRicevuto: ChatMessage | null;
  /**
   * Gli istanti dei propri invii ancora nella finestra del limite a raffica.
   * Non si azzerano ne' cambiando stanza ne' uscendo dalla partita: il server
   * conta per socket, e il socket sopravvive alla partita. Azzerandoli qui, chi
   * esce e riscrive subito altrove vedrebbe il messaggio accettato dallo
   * schermo, rifiutato dal server e perso.
   */
  inviiRecenti: number[];
  /** Il limite a raffica e' scattato: lo dice la chat finche' non si libera un posto. */
  limiteRaggiunto: boolean;

  setChatContext: (ctx: ChatContext) => void;
  clearChatContext: () => void;
  receiveMessage: (message: ChatMessage) => void;
  /**
   * Rimette in lista la conversazione che il server tiene nella room, dopo una
   * disconnessione o un riavvio dell'app. Va chiamata DOPO `setChatContext`:
   * prima, un cambio di room la cancellerebbe subito.
   */
  restoreMessages: (transcript: ChatMessage[]) => void;
  /** `true` se il messaggio e' partito; `false` se non e' partito (il campo tiene il testo). */
  send: (message: string) => boolean;
  /**
   * Il limite a raffica e' scattato — dallo schermo o dal server
   * (`CHAT_RATE_LIMITED`). L'avviso resta `attesaMs`, poi si spegne da solo.
   */
  segnalaLimite: (attesaMs?: number) => void;
  toggle: () => void;
  setActive: (active: boolean) => void;
  markOpponentReported: () => void;
  setOpponentLeft: (roomId: string, left: boolean) => void;
  /** Il server avvisa: la chat chiude a `closesAt`. */
  segnalaChiusuraChat: (roomId: string, closesAt: number) => void;
  /**
   * La chat e' chiusa. `roomId` a `null` salta la guardia: lo usa il percorso
   * d'errore (`CHAT_ROOM_CLOSED`), che la room non la porta ed e' per costruzione
   * quella della conversazione corrente.
   */
  chiudiChat: (roomId: string | null) => void;
  /**
   * Il conto alla rovescia e' arrivato a zero ma la chiusura non e' arrivata: la
   * finestra si e' allungata. Si spegne l'avviso, non la chat.
   */
  scadeChiusuraChat: () => void;
  salutoLetto: () => void;
  chiudiSaluto: () => void;
}

const EMPTY_CONTEXT: ChatContext = {
  roomId: null,
  playerColor: null,
  canChat: false,
  source: 'online',
};

/**
 * Il margine dello schermo sulla finestra del limite a raffica: abbastanza per
 * il ritardo di rete fra l'invio e l'arrivo al server.
 */
const MARGINE_RETE_MS = 250;

/** Il timer che spegne l'avviso del limite: uno solo, il piu' recente vince. */
let timerLimite: ReturnType<typeof setTimeout> | null = null;

export const useChatStore = create<ChatStore>((set, get) => ({
  ...EMPTY_CONTEXT,
  messages: [],
  isOpen: true,
  unreadCount: 0,
  opponentReported: false,
  opponentLeft: false,
  chatChiusa: false,
  chatClosesAt: null,
  salutoRicevuto: null,
  inviiRecenti: [],
  limiteRaggiunto: false,

  /**
   * Punto unico d'ingresso del contesto, e unico punto in cui il transcript si
   * azzera: solo quando il roomId cambia verso un'altra room. Una regola sola
   * copre due esigenze opposte — l'arena incatena partite con room sempre nuove
   * (i messaggi della precedente non devono trascinarsi), mentre la rivincita
   * online riusa la STESSA room lato server (room.startRematch), quindi lì
   * transcript e mute sopravvivono come hanno sempre fatto.
   */
  setChatContext: (ctx: ChatContext) => {
    const isNewRoom = ctx.roomId !== null && ctx.roomId !== get().roomId;
    set({
      ...ctx,
      ...(isNewRoom
        ? { messages: [], unreadCount: 0, opponentReported: false, opponentLeft: false, chatChiusa: false, chatClosesAt: null, isOpen: true, salutoRicevuto: null }
        : {}),
    });
  },

  clearChatContext: () => {
    set({
      ...EMPTY_CONTEXT,
      messages: [],
      unreadCount: 0,
      opponentReported: false,
      opponentLeft: false,
      chatChiusa: false,
      chatClosesAt: null,
      isOpen: true,
      salutoRicevuto: null,
      limiteRaggiunto: false,
    });
    if (timerLimite) {
      clearTimeout(timerLimite);
      timerLimite = null;
    }
  },

  receiveMessage: (message: ChatMessage) => {
    const { messages, isOpen, playerColor, opponentReported, roomId } = get();
    // Messaggio di un'altra room: in arena la partita successiva viene accoppiata
    // pochi secondi dopo la fine della precedente, e un messaggio in ritardo non
    // deve comparire nella conversazione sbagliata.
    if (roomId !== null && message.roomId !== roomId) return;

    // Limit messages to prevent memory bloat
    const newMessages = [...messages, message].slice(-CHAT_CONFIG.MAX_MESSAGES_PER_ROOM);
    // Increment unread count if chat is closed and message is from opponent.
    // A reported opponent is muted: their messages are not rendered, so the
    // badge must not call the player back to read something they hid.
    // Per identità, non per colore: dopo una rivincita i colori sono scambiati e
    // la conversazione è la stessa (vedi `eMessaggioMio`).
    const isFromOpponent = !eMessaggioMio(message, playerColor);
    const raisesUnread = isFromOpponent && !isOpen && !opponentReported;
    /*
     * Il saluto d'apertura si mette da parte perche' la partita lo mostri fuori
     * dalla chat. Il conteggio dei non letti NON cambia qui: e' `salutoLetto()`
     * a ritirare il badge, e solo se la pastiglia lo ha davvero mostrato. Farlo
     * qui vorrebbe dire far dipendere il badge dall'ordine in cui arrivano il
     * messaggio e il primo render della partita — una corsa — e togliere dal
     * conto un messaggio che nessuno ha ancora visto.
     *
     * Un avversario segnalato e' muto: niente pastiglia, come niente badge.
     */
    const eSaluto = isFromOpponent && !opponentReported && message.presetKey === SALUTO_PRESET_KEY;
    set({
      messages: newMessages,
      unreadCount: raisesUnread ? get().unreadCount + 1 : get().unreadCount,
      ...(eSaluto ? { salutoRicevuto: message } : {}),
    });
  },

  /**
   * Sostituisce, non accoda: un solo rientro può passare da entrambi i percorsi
   * del server (`game:rejoined` e il callback di `game:check-active`), e due
   * ripristini di fila devono lasciare la stessa conversazione, non il doppio.
   *
   * Non tocca `unreadCount`: sono righe già lette, il badge non deve suonare.
   */
  restoreMessages: (transcript: ChatMessage[]) => {
    if (transcript.length === 0) return;
    const { roomId } = get();
    // Stessa guardia di receiveMessage: in arena la partita dopo arriva pochi
    // secondi dopo, e un transcript in ritardo non deve finire nella
    // conversazione sbagliata. Senza contesto (rientro che arriva prima del
    // setChatContext) si accetta: ci penserà il cambio di room a ripulire.
    const mine = roomId === null ? transcript : transcript.filter((m) => m.roomId === roomId);
    if (mine.length === 0) return;
    set({ messages: mine.slice(-CHAT_CONFIG.MAX_MESSAGES_PER_ROOM) });
  },

  send: (message: string) => {
    const { roomId, canChat, opponentLeft } = get();
    if (!roomId || !canChat || opponentLeft) {
      return false;
    }

    const trimmed = message.trim();
    if (!trimmed || trimmed.length > CHAT_CONFIG.MAX_MESSAGE_LENGTH) {
      return false;
    }

    /*
     * Il limite a raffica, controllato QUI e non solo sul server: un messaggio
     * che il server rifiuta non torna indietro, e prima spariva — il campo si
     * era gia' svuotato, e in torneo e in arena non lo diceva nessuno.
     */
    const adesso = Date.now();
    // Con un margine: il server misura quando il messaggio ARRIVA, e un primo
    // messaggio lento a viaggiare gli farebbe sembrare il sesto troppo presto.
    const raffica = controllaRafficaChat(get().inviiRecenti, adesso, MARGINE_RETE_MS);
    if (!raffica.ammesso) {
      set({ inviiRecenti: raffica.recenti });
      get().segnalaLimite(raffica.attesaMs);
      return false;
    }
    set({ inviiRecenti: [...raffica.recenti, adesso] });

    sendGameChat(roomId, trimmed);
    return true;
  },

  segnalaLimite: (attesaMs = CHAT_CONFIG.RATE_LIMIT_WINDOW_MS) => {
    if (timerLimite) clearTimeout(timerLimite);
    set({ limiteRaggiunto: true });
    timerLimite = setTimeout(() => {
      timerLimite = null;
      set({ limiteRaggiunto: false });
    }, Math.max(0, attesaMs));
  },

  setOpponentLeft: (roomId: string, left: boolean) => {
    // Un avviso di un'altra room (una partita precedente) non tocca questa.
    if (get().roomId !== roomId) return;
    set({ opponentLeft: left });
  },

  segnalaChiusuraChat: (roomId: string, closesAt: number) => {
    // Stessa guardia di `setOpponentLeft`: in arena la partita dopo arriva pochi
    // secondi dopo, e il preavviso della precedente non deve finire su questa.
    if (get().roomId !== roomId) return;
    set({ chatClosesAt: closesAt });
  },

  chiudiChat: (roomId: string | null) => {
    if (roomId !== null && get().roomId !== roomId) return;
    set({ canChat: false, chatChiusa: true, chatClosesAt: null });
  },

  scadeChiusuraChat: () => {
    set({ chatClosesAt: null });
  },

  toggle: () => {
    const { isOpen } = get();
    set({
      isOpen: !isOpen,
      unreadCount: !isOpen ? 0 : get().unreadCount, // Clear unread when opening
    });
  },

  // Sync chat visibility to the active sidebar tab. When chat becomes active
  // it counts as "open" (clearing unread); when inactive, incoming opponent
  // messages bump unreadCount so the tab badge appears.
  setActive: (active: boolean) => {
    set({
      isOpen: active,
      unreadCount: active ? 0 : get().unreadCount,
    });
  },

  // Called after a successful abuse report: hide the reported opponent's
  // messages for the rest of the session. Client-side only — the server keeps
  // delivering them, a report is not a ban.
  markOpponentReported: () => {
    set({ opponentReported: true, unreadCount: 0 });
  },

  /**
   * La pastiglia ha mostrato il saluto: quella riga e' LETTA, e il badge dei non
   * letti si ritira di uno.
   *
   * Senza questo, salutare qualcuno gli lasciava sempre un `1` sulla chat — su
   * mobile la chat e' un pannello chiuso, quindi `raisesUnread` e' vero ogni
   * volta — cioe' un invito ad aprirla per rileggere quello che aveva appena
   * letto. Il pavimento a zero perche' il badge puo' essere stato azzerato
   * altrove (apertura della chat, segnalazione) tra l'arrivo e il render.
   */
  salutoLetto: () => {
    set({ unreadCount: Math.max(0, get().unreadCount - 1) });
  },

  /** Mostrato, risposto, chiuso a mano o scartato fuori finestra: via. */
  chiudiSaluto: () => {
    set({ salutoRicevuto: null });
  },
}));
